import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaApple, FaAndroid, FaTimes, FaMobileAlt, FaCheck, FaRocket } from 'react-icons/fa';
import { useAppSelector } from '../../store/hooks';
import { obterIdExpo } from '../../services/seguranca';

// --- Animações ---
const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

// --- Styled Components ---

const AlertWrapper = styled.div`
  width: 100%;
  padding: 20px;
  animation: ${slideIn} 0.5s ease-out forwards;
`;

const CardContainer = styled.div`
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid #f1f5f9;

  @media (min-width: 850px) {
    flex-direction: row;
    min-height: 280px; /* Altura controlada para não ocupar a tela toda */
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 50;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s ease;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }
`;

// --- Lado Esquerdo (Visual / Info) ---
const LeftPanel = styled.div`
  flex: 1.2; /* Um pouco maior para caber o texto */
  background: linear-gradient(135deg, #1e40af 0%, #172554 100%); /* Gradiente Azul Profundo */
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

// Elementos decorativos de fundo
const DecoCircle = styled.div<{ size: string; top?: string; left?: string; right?: string; bottom?: string }>`
  position: absolute;
  width: ${props => props.size};
  height: ${props => props.size};
  background: white;
  opacity: 0.05;
  border-radius: 50%;
  top: ${props => props.top};
  left: ${props => props.left};
  right: ${props => props.right};
  bottom: ${props => props.bottom};
  pointer-events: none;
`;

const ContentLeft = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  gap: 20px;
  align-items: center;
  
  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const IconGlass = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);

  svg {
    font-size: 28px;
    color: white;
  }
`;

const TextBlock = styled.div`
  color: white;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
  color: white;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #bfdbfe; /* Blue 200 */
  line-height: 1.5;
  margin-bottom: 16px;
  max-width: 450px;
`;

const TagContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 600px) {
    justify-content: center;
  }
`;

const FeatureTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.1);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: white;
`;

// --- Lado Direito (Ação) ---
const RightPanel = styled.div`
  flex: 1;
  background: white;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center; /* Centralizado horizontalmente */
  position: relative;
  border-left: 1px solid #f1f5f9;
`;

// Efeito de Blob atrás do ícone da direita
const BlobEffect = styled.div`
  position: absolute;
  width: 120px;
  height: 120px;
  background: #dbeafe;
  border-radius: 50%;
  filter: blur(25px);
  opacity: 0.6;
  z-index: 0;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const RocketContainer = styled.div`
  position: relative;
  z-index: 10;
  font-size: 48px;
  color: #2563eb;
  margin-bottom: 16px;
  animation: ${float} 3s ease-in-out infinite;
  
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
  position: relative;
  z-index: 10;
`;

const ActionText = styled.p`
  font-size: 13px;
  color: #64748b;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  z-index: 10;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 10px;
  position: relative;
  z-index: 10;
  flex-wrap: wrap;
  justify-content: center;
`;

const StoreButton = styled.a<{ $store: 'ios' | 'android' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  
  ${props => props.$store === 'ios' && css`
    background-color: #0f172a;
    color: white;
    &:hover {
      background-color: #334155;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    }
  `}

  ${props => props.$store === 'android' && css`
    background-color: #00875f;
    color: white;
    &:hover {
      background-color: #00a072;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,135,95,0.3);
    }
  `}

  div {
    display: flex;
    flex-direction: column;
    line-height: 1;
  }
`;

const SmallText = styled.span`
  font-size: 9px;
  text-transform: uppercase;
  opacity: 0.8;
  margin-bottom: 2px;
`;

const BigText = styled.span`
  font-size: 13px;
  font-weight: 600;
`;

// Links
const IOS_LINK = "https://apps.apple.com/br/app/locasmartpro-app-locador/id6757645086";
const ANDROID_LINK = "https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadorasapplocadores&pcampaignid=web_share";

export function AppDownloadAlert() {
  const token = useAppSelector(state => state.token);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Lógica para verificar se deve mostrar
    const hasExpoId = obterIdExpo(token);
    const isDismissed = localStorage.getItem('appDownloadAlert_inline_v1');

    if (!hasExpoId && !isDismissed) {
      setVisible(true);
    }
  }, [token]);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem('appDownloadAlert_inline_v1', 'true');
  };

  if (!visible) return null;

  return (
    <AlertWrapper>
      <CardContainer>
        <CloseButton onClick={handleDismiss} title="Fechar alerta">
          <FaTimes />
        </CloseButton>

        {/* Lado ESQUERDO: Visual Premium (Gradiente) */}
        <LeftPanel>
          <DecoCircle size="200px" top="-50px" left="-50px" />
          <DecoCircle size="120px" bottom="-20px" right="20px" />
          
          <ContentLeft>
            <IconGlass>
              <FaMobileAlt />
            </IconGlass>
            
            <TextBlock>
              <Title>Novo App LocaSmartPro</Title>
              <Subtitle>
                Tenha o controle total da sua locadora na palma da mão. 
                Mais agilidade para vistorias, contratos, gestão de clientes e Pagamentos.
              </Subtitle>
              
              <TagContainer>
                <FeatureTag>
                  <FaCheck size={10} /> Vistorias
                </FeatureTag>
                <FeatureTag>
                  <FaCheck size={10} /> Notificações em tempo real
                </FeatureTag>
                <FeatureTag>
                  <FaCheck size={10} /> Dashboard Mobile
                </FeatureTag>
                <FeatureTag>
                  <FaCheck size={10} /> Tudo na Palma da sua Mão!
                </FeatureTag>
              </TagContainer>
            </TextBlock>
          </ContentLeft>
        </LeftPanel>

        {/* Lado DIREITO: Call to Action (Branco) */}
        <RightPanel>
          <BlobEffect />
          <RocketContainer>
            <FaRocket />
          </RocketContainer>

          <ActionTitle>Baixe Grátis</ActionTitle>
          <ActionText>Disponível para Android e iOS</ActionText>

          <ButtonsRow>
            <StoreButton 
              href={IOS_LINK} 
              target="_blank" 
              rel="noopener noreferrer" 
              $store="ios"
            >
              <FaApple size={20} />
              <div>
                <SmallText>Baixe para Iphone</SmallText>
                <BigText>App Store</BigText>
              </div>
            </StoreButton>

            <StoreButton 
              href={ANDROID_LINK} 
              target="_blank" 
              rel="noopener noreferrer" 
              $store="android"
            >
              <FaAndroid size={20} />
              <div>
                <SmallText>Baixe para Android</SmallText>
                <BigText>Google Play</BigText>
              </div>
            </StoreButton>
          </ButtonsRow>
        </RightPanel>
      </CardContainer>
    </AlertWrapper>
  );
}